// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The three shapes a match can take. A mode has to change how the game PLAYS, not a number — and the
// honest test of that is **what the thumb is solving for**:
//
//   crossfire → a timing problem. Every position is visible; the only secret is the five seconds
//               they just wrote. The whole skill is arranging to be already stationary when their
//               watch sweeps past, which you can only attempt because you can see where they are
//               looking.
//   ambush    → an information problem. You see an enemy only while one of your own units can see
//               it, and FIRING GIVES YOU AWAY for the rest of the turn. Not shooting becomes a real
//               option, which is not a sentence that means anything in Crossfire.
//   roving    → a geography problem. The mast moves every second turn and the next one is marked a
//               turn ahead, so no angle is ever worth owning twice. It is the mode where the slow
//               long-range unit stops being king and the scout earns its keep.
//
// Ambush deserves a note, because it is the one that could have been a desync. It changes NOTHING
// about the simulation: both peers resolve the identical five seconds and each renders its own
// side's knowledge of them. A rules branch on "what can this peer see" would be a hidden branch, and
// this fleet has learned exactly what those cost.
//
// `modeOf()` validates any id arriving from outside (a URL, the wire, localStorage) with
// `Object.hasOwn` rather than `MODES[id] ||`: a stored value of "constructor" is a real key on a
// plain object and would otherwise reach the map generator as a function.

export type ModeId = 'crossfire' | 'ambush' | 'roving';

export interface Mode {
  id: ModeId;
  name: string;
  blurb: string;
  /** One line for the help card, phrased as what the mode asks OF YOU. */
  rule: string;
  /** Units per side — one of each type, in type order. */
  units: number;
  /** Solid blocks stamped per half. */
  blocks: number;
  /** Rails stamped per half: they stop feet and not sight, which is what stops the board being rooms. */
  rails: number;
  /** Acceptable band for `laneScore` — how open the board is allowed to be. */
  lanes: [number, number];
  /** You see only what your units see, and firing reveals you. */
  fog: boolean;
  /** The mast moves every N turns (0 = never). */
  roving: number;
  /** Ticks of uncontested holding that win the match outright. */
  holdTarget: number;
  /** Turns before the match is decided on what has been banked. */
  turns: number;
}

const LIST: Mode[] = [
  {
    id: 'crossfire',
    name: 'Crossfire',
    blurb: '3 v 3 · everything visible but the plan',
    rule: 'Hold the mast. You can see where every unit is and which way it is watching — the only secret is the next five seconds.',
    units: 3,
    blocks: 7,
    rails: 3,
    lanes: [0.3, 0.52],
    fog: false,
    roving: 0,
    holdTarget: 240,
    turns: 10,
  },
  {
    id: 'ambush',
    name: 'Ambush',
    blurb: '3 v 3 · you see only what your squad sees',
    rule: 'Hold the mast in the dark. You see an enemy only while one of yours can — and firing gives you away for the rest of the turn.',
    units: 3,
    blocks: 10,
    rails: 4,
    lanes: [0.16, 0.40],
    fog: true,
    roving: 0,
    holdTarget: 240,
    turns: 10,
  },
  {
    id: 'roving',
    name: 'Roving',
    blurb: '3 v 3 · two masts, and they keep moving',
    rule: 'Two masts stand at once, one in each half, and they move every second turn. No angle is worth owning twice.',
    units: 3,
    blocks: 9,
    rails: 4,
    lanes: [0.2, 0.48],
    fog: false,
    roving: 2,
    holdTarget: 220,
    turns: 10,
  },
];

export const MODES: Record<ModeId, Mode> = Object.fromEntries(LIST.map((m) => [m.id, m])) as Record<ModeId, Mode>;
export const MODE_IDS: ModeId[] = LIST.map((m) => m.id);
export const DEFAULT_MODE: ModeId = 'crossfire';

/** Validate an id from anywhere untrusted. Never `MODES[id] || DEFAULT`. */
export function modeOf(id: string | undefined | null): Mode {
  return typeof id === 'string' && Object.hasOwn(MODES, id) ? MODES[id as ModeId] : MODES[DEFAULT_MODE];
}
